import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { FontCategoryDto, FontOwnershipFilterDto } from "@repo/api/font";
import { EditorApi } from "../api/editor-api";

const FONTS_PAGE_SIZE = 25;

type FontsCursor =
  | { isTeamFont: boolean; family: string; externalId: string }
  | undefined;

export function usePagedTeamFontsQuery(
  teamExternalId: string,
  filters: {
    search: string;
    ownership: FontOwnershipFilterDto;
    category?: FontCategoryDto;
  },
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: fontQueryKeys.pagedTeamFonts(teamExternalId, filters),
    queryFn: ({ pageParam }) =>
      EditorApi.getPagedFonts(teamExternalId, {
        pageSize: FONTS_PAGE_SIZE,
        search: filters.search || undefined,
        ownership: filters.ownership,
        category: filters.category,
        cursorIsTeamFont: pageParam?.isTeamFont,
        cursorFamily: pageParam?.family,
        cursorExternalId: pageParam?.externalId,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as FontsCursor,
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch fonts",
    },
  });
}

export function useFontsByExternalIdsQuery(
  teamExternalId: string,
  externalIds: string[],
  options?: { enabled?: boolean },
) {
  const sortedIds = [...externalIds].sort();

  return useQuery({
    queryKey: fontQueryKeys.fontsByExternalIds(teamExternalId, sortedIds),
    queryFn: () => EditorApi.getFontsByExternalIds(teamExternalId, sortedIds),
    enabled: (options?.enabled ?? true) && sortedIds.length > 0,
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch fonts",
    },
  });
}

export const fontQueryKeys = {
  teamFonts: (teamExternalId: string) => ["fonts", "paged", teamExternalId],
  pagedTeamFonts: (
    teamExternalId: string,
    filters: {
      search: string;
      ownership: FontOwnershipFilterDto;
      category?: FontCategoryDto;
    },
  ) => ["fonts", "paged", teamExternalId, filters],
  fontsByExternalIds: (teamExternalId: string, externalIds: string[]) => [
    "fonts",
    "by-ids",
    { teamExternalId, externalIds },
  ],
};
